/**
 * Glue between the layer spine (`_layer.ts`) and `hydrogen/anchor`, shared by
 * every popper-style primitive (popover, tooltip, hover card, select, combobox).
 *
 * {@link mountFloating} does what Radix's `Popper` + `Presence` do:
 *
 * 1. un-hides the content and mounts it as a layer (portal, stack, dismissal);
 * 2. positions the layer's wrapper with a fixed-position transform, so the
 *    content keeps its own `transform` for entrance animations, and publishes
 *    `data-side` / `data-align` and the measurement variables on the content;
 * 3. moves initial focus *after* the first positioning, so focusing never
 *    scrolls the page to an unpositioned element;
 * 4. on close, waits for a running exit animation before tearing down.
 *
 * It also holds the small amount of state logic every overlay repeats:
 * controlled/uncontrolled open state and reading placement from attributes.
 *
 * Placement attributes read from the content element (so a Worker can emit them
 * as plain HTML; each is overridden by the matching option):
 * `data-prefer-side="top|right|bottom|left"`, `data-prefer-align="start|center|end"`,
 * `data-side-offset="<px>"`, `data-align-offset="<px>"`,
 * `data-collision-padding="<px>"`, `data-avoid-collisions="false"`,
 * `data-sticky="partial|always"`.
 *
 * Published custom properties (the one source of truth; written by
 * `hydrogen/anchor` on the content every time it positions, and by Select's
 * item-aligned mode, which has its own geometry). shadcn's `--radix-<x>-…` and
 * Base UI's names are rewritten onto these by the converter, never published:
 *
 * | property             | value                                                     |
 * | -------------------- | --------------------------------------------------------- |
 * | `--anchor-width`     | the anchor's measured width (Radix `trigger-width`)       |
 * | `--anchor-height`    | the anchor's measured height (Radix `trigger-height`)     |
 * | `--available-width`  | room left for the content (Radix `content-available-width`)  |
 * | `--available-height` | room left for the content (Radix `content-available-height`) |
 * | `--transform-origin` | where an entrance animation grows from, from the placed side/align (Radix `content-transform-origin`) |
 */
import { createAnchoredPosition, } from 'hydrogen/anchor';
import { FOCUSABLE_SELECTOR } from 'hydrogen/focus-trap';
import { generateId } from 'hydrogen/id';
import { mountLayer } from './_layer.js';
import { exitWhenDone } from './_presence.js';
/** Open state with Radix's controlled/uncontrolled semantics. `apply` runs on every change. */
export function createOpenState(options, apply) {
    const state = {
        open: options.open ?? options.defaultOpen ?? false,
        controlled: options.open !== undefined,
        onOpenChange: options.onOpenChange,
    };
    const set = (next) => {
        if (state.open === next)
            return;
        state.open = next;
        apply(next);
    };
    return {
        get isOpen() {
            return state.open;
        },
        set,
        request(next) {
            if (state.open === next)
                return;
            state.onOpenChange?.(next);
            if (!state.controlled)
                set(next);
        },
        update(next) {
            if (next.onOpenChange !== undefined)
                state.onOpenChange = next.onOpenChange;
            if (next.open !== undefined) {
                state.controlled = true;
                set(next.open);
            }
        },
    };
}
/** Wraps open state in the public controller shape. */
export function openController(state, destroy) {
    return {
        open: () => state.request(true),
        close: () => state.request(false),
        toggle: () => state.request(!state.isOpen),
        update: (next) => state.update(next),
        get isOpen() {
            return state.isOpen;
        },
        destroy,
    };
}
/** An id for `element`, generated and written when the markup has none. */
export function ensureId(element, prefix) {
    if (!element.id)
        element.id = `${prefix}-${generateId()}`;
    return element.id;
}
export function createDisposer() {
    const teardowns = [];
    const state = { disposed: false };
    return {
        on(target, type, handler, options) {
            const listener = handler;
            target.addEventListener(type, listener, options);
            teardowns.push(() => target.removeEventListener(type, listener, options));
        },
        add(teardown) {
            teardowns.push(teardown);
        },
        dispose() {
            if (state.disposed)
                return;
            state.disposed = true;
            for (const teardown of teardowns.splice(0).reverse())
                teardown();
        },
        get disposed() {
            return state.disposed;
        },
    };
}
const SIDES = ['top', 'right', 'bottom', 'left'];
const ALIGNS = ['start', 'center', 'end'];
function numberAttribute(element, name) {
    const raw = element.getAttribute(name);
    if (raw === null || raw.trim() === '')
        return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
}
/**
 * Placement from `data-*` attributes on the content, overridden by `overrides`.
 * `data-side` and `data-align` are *outputs* (the resolved placement), so the
 * preferences live in `data-prefer-side` / `data-prefer-align`.
 */
export function readPlacement(content, overrides = {}, defaults = {}) {
    const side = content.getAttribute('data-prefer-side');
    const align = content.getAttribute('data-prefer-align');
    const sticky = content.getAttribute('data-sticky');
    const attributes = {
        side: side && SIDES.includes(side) ? side : undefined,
        align: align && ALIGNS.includes(align) ? align : undefined,
        sideOffset: numberAttribute(content, 'data-side-offset'),
        alignOffset: numberAttribute(content, 'data-align-offset'),
        collisionPadding: numberAttribute(content, 'data-collision-padding'),
        avoidCollisions: content.getAttribute('data-avoid-collisions') === 'false' ? false : undefined,
        sticky: sticky === 'partial' || sticky === 'always' ? sticky : undefined,
    };
    const merged = { ...defaults };
    for (const source of [attributes, overrides]) {
        for (const [key, value] of Object.entries(source)) {
            if (value !== undefined)
                merged[key] = value;
        }
    }
    return merged;
}
/** Focuses per an {@link InitialFocus} choice. */
export function focusInto(content, choice) {
    if (choice === false)
        return;
    let target;
    if (choice === 'content')
        target = content;
    else if (choice === 'first') {
        target =
            content.querySelector('[autofocus], [data-autofocus]') ??
                content.querySelector(FOCUSABLE_SELECTOR) ??
                content;
    }
    else
        target = choice;
    if (target === content && !content.hasAttribute('tabindex')) {
        content.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
}
/** Mounts `content` as a positioned floating layer. */
export function mountFloating(options) {
    const { content, anchor, positioner, placement, initialFocus, zIndex, ...layerOptions } = options;
    content.hidden = false;
    const layer = mountLayer({ ...layerOptions, content, initialFocus: false, restoreFocus: false });
    const wrapper = content.parentElement;
    wrapper.style.zIndex = String(zIndex ?? 50);
    const position = positioner?.(wrapper) ??
        createAnchoredPosition(wrapper, anchor ?? null, {
            ...placement,
            content,
            apply: 'fixed',
        });
    focusInto(content, initialFocus ?? (options.modal ? 'first' : false));
    const state = { torn: false, closing: false };
    const cancelExit = [];
    const floating = {
        content,
        wrapper,
        layer,
        update: () => {
            position.update();
        },
        get closing() {
            return state.closing;
        },
        destroy() {
            if (state.torn)
                return;
            state.torn = true;
            for (const cancel of cancelExit.splice(0))
                cancel();
            position.destroy();
            layer.destroy();
            content.hidden = true;
        },
        close(done) {
            if (state.torn) {
                done?.();
                return;
            }
            if (state.closing)
                return;
            state.closing = true;
            cancelExit.push(exitWhenDone(content, () => {
                floating.destroy();
                done?.();
            }));
        },
    };
    return floating;
}
/** Document-order tabbable elements outside `exclude`, for Tab-out handling. */
export function tabbablesOutside(exclude) {
    return Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => !exclude.contains(element) &&
        !element.hasAttribute('inert') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        !element.closest('[inert], [hidden]'));
}
/** True for a touch pointer, which opens neither tooltips nor hover cards. */
export function isTouchPointer(event) {
    return event.pointerType === 'touch';
}
export function createTimer() {
    const state = { id: undefined };
    return {
        start(ms, run) {
            clearTimeout(state.id);
            state.id = setTimeout(run, ms);
        },
        clear() {
            clearTimeout(state.id);
            state.id = undefined;
        },
    };
}
